import React, { useEffect, useState } from 'react';
import { Scale, CheckCircle2, AlertCircle, Loader, Send } from 'lucide-react';
import { getPublicIntakeForm, submitPublicIntake } from '../lib/db';

// ─── Field renderer ────────────────────────────────────────────────────────────
function FormField({ field, value, onChange, error }) {
  const base =
    'w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition ' +
    (error ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white');

  const label = (
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {field.label}
      {field.required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  let input;
  switch (field.type) {
    case 'textarea':
      input = (
        <textarea
          rows={4}
          className={base + ' resize-y'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
        />
      );
      break;

    case 'select':
      input = (
        <select
          className={base + ' cursor-pointer'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— Select —</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
      break;

    case 'checkbox':
      return (
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id={field.key}
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          />
          <label htmlFor={field.key} className="text-sm text-gray-700 cursor-pointer">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
      );

    case 'date':
      input = (
        <input
          type="date"
          className={base}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;

    case 'tel':
    case 'phone':
      input = (
        <input
          type="tel"
          className={base}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || 'e.g. +1 555 000 0000'}
        />
      );
      break;

    case 'email':
      input = (
        <input
          type="email"
          className={base}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || 'you@example.com'}
        />
      );
      break;

    case 'number':
      input = (
        <input
          type="number"
          className={base}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
        />
      );
      break;

    default: // text
      input = (
        <input
          type="text"
          className={base}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
        />
      );
  }

  return (
    <div>
      {label}
      {input}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function PublicIntakeForm({ formId }) {
  const [form,       setForm]       = useState(null);
  const [loadError,  setLoadError]  = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [answers,    setAnswers]    = useState({});
  const [fieldErrors,setFieldErrors]= useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [submitError,setSubmitError]= useState(null);

  // Load form on mount
  useEffect(() => {
    if (!formId) {
      setLoadError('No form ID provided.');
      setLoading(false);
      return;
    }
    getPublicIntakeForm(formId).then(({ data, error }) => {
      if (error || !data) {
        setLoadError('This intake form is not available or has been closed.');
      } else {
        setForm(data);
        // Initialise answer map so all fields start as empty strings
        const initial = {};
        (data.fields || []).forEach((f) => { initial[f.key] = ''; });
        setAnswers(initial);
      }
      setLoading(false);
    });
  }, [formId]);

  const validate = () => {
    const errors = {};
    (form.fields || []).forEach((f) => {
      if (f.required) {
        const val = answers[f.key];
        if (val === undefined || val === null || String(val).trim() === '') {
          errors[f.key] = `${f.label} is required.`;
        }
      }
      if (f.type === 'email' && answers[f.key]) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answers[f.key])) {
          errors[f.key] = 'Please enter a valid email address.';
        }
      }
    });
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Scroll to first error
      const firstKey = Object.keys(errors)[0];
      document.getElementById(`field-${firstKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);
    const { error } = await submitPublicIntake(form.id, form.firm_id, answers);
    setSubmitting(false);

    if (error) {
      setSubmitError(error.message || 'Submission failed. Please try again.');
    } else {
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <Loader size={32} className="animate-spin text-indigo-500" />
          <p className="text-sm">Loading form…</p>
        </div>
      </div>
    );
  }

  // ── Error state (form not found / inactive) ──
  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center space-y-4">
          <AlertCircle size={40} className="text-red-400 mx-auto" />
          <h2 className="text-lg font-semibold text-gray-800">Form Unavailable</h2>
          <p className="text-sm text-gray-500">{loadError}</p>
        </div>
      </div>
    );
  }

  const firm = form.firm || {};

  // ── Success state ──
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10 max-w-md w-full text-center space-y-5">
          {firm.logo_url ? (
            <img
              src={firm.logo_url}
              alt={firm.name || 'Firm logo'}
              className="h-12 w-auto max-w-[180px] object-contain mx-auto"
            />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center mx-auto">
              <Scale size={22} className="text-white" />
            </div>
          )}
          <CheckCircle2 size={52} className="text-green-500 mx-auto" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Thank you!</h2>
            <p className="text-sm text-gray-500 mt-1">
              Your information has been received. A member of our team will be in touch shortly.
            </p>
          </div>
          {firm.name && (
            <p className="text-xs text-gray-400 border-t pt-4">
              Submitted to <span className="font-medium text-gray-600">{firm.name}</span>
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Form ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
      {/* Header bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          {firm.logo_url ? (
            <img
              src={firm.logo_url}
              alt={firm.name}
              className="h-9 w-auto object-contain"
            />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
              <Scale size={18} className="text-white" />
            </div>
          )}
          {firm.name && (
            <span className="text-sm font-semibold text-gray-700">{firm.name}</span>
          )}
        </div>
      </div>

      {/* Form card */}
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Form header */}
          <div className="bg-indigo-600 px-6 py-6 md:px-8">
            <h1 className="text-xl font-bold text-white">{form.name}</h1>
            {form.description && (
              <p className="text-indigo-100 text-sm mt-1">{form.description}</p>
            )}
          </div>

          {/* Fields */}
          <form onSubmit={handleSubmit} className="px-6 py-8 md:px-8 space-y-6" noValidate>
            {(form.fields || []).map((field) => (
              <div key={field.key} id={`field-${field.key}`}>
                <FormField
                  field={field}
                  value={answers[field.key] ?? ''}
                  onChange={(val) => {
                    setAnswers((prev) => ({ ...prev, [field.key]: val }));
                    setFieldErrors((prev) => ({ ...prev, [field.key]: undefined }));
                  }}
                  error={fieldErrors[field.key]}
                />
              </div>
            ))}

            {submitError && (
              <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium py-3 px-6 rounded-lg transition flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><Loader size={16} className="animate-spin" /> Submitting…</>
              ) : (
                <><Send size={16} /> Submit</>
              )}
            </button>

            <p className="text-center text-xs text-gray-400">
              Your information is kept confidential and used only to assess your matter.
            </p>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by <span className="font-medium">DocketFlow</span>
        </p>
      </div>
    </div>
  );
}
